import React from 'react';
import {
  Drawer, Box, IconButton, List, ListItem, ListItemIcon, ListItemText,
  Divider, Avatar, Typography, ListSubheader, alpha, useTheme
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EmailIcon from '@mui/icons-material/Email';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ScaleAnimation } from '../../animations/MotionWrapper';
import { useAuth } from '../../../context/AuthContext';
import NavLogo from './NavLogo';
import { ROUTES } from '../../../constants/routes';
import { ROLES, APP_VERSION, FEATURES } from '../../../constants';
import ThemeToggle from './ThemeToggle';

/**
 * MobileDrawer - Mobile navigation drawer
 * Displays full navigation menu in a slide-out drawer for mobile devices
 *
 * @param {boolean} open - Whether the drawer is open
 * @param {Function} onClose - Callback to close the drawer
 */
const MobileDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const isActive = (path) => {
    if (path === ROUTES.HOME) {
      return location.pathname === ROUTES.HOME;
    }
    return location.pathname.startsWith(path);
  };

  // Build navigation items with role-based sections
  const isOfficeOrAdmin = currentUser?.role === ROLES.ADMIN || currentUser?.role === ROLES.OFFICE || currentUser?.role === ROLES.DEV;
  const isAdmin = currentUser?.role === ROLES.ADMIN || currentUser?.role === ROLES.DEV;

  // Secondary navigation items - items NOT in the bottom nav
  // Bottom nav has: Dashboard, Calendar, Work Hours (employee) / Clients (office), Profile
  const mainNavItems = currentUser ? [
    // WEITERE OPTIONEN section
    { type: 'section', label: 'WEITERE OPTIONEN' },
    // Terminberichte (ASB) - personal reports page, for everyone
    { text: 'Terminberichte', path: ROUTES.REPORTS, icon: <DescriptionIcon /> },
    // Work Hours - for office/admin (employees have it in bottom nav)
    ...(isOfficeOrAdmin ? [
      { text: 'Arbeitsstunden', path: ROUTES.WORK_HOURS, icon: <AccessTimeIcon /> }
    ] : []),
    // Kunden - for standard users (office/admin have it in bottom nav)
    ...(!isOfficeOrAdmin ? [
      { text: 'Kunden', path: ROUTES.CLIENTS, icon: <ArticleIcon /> }
    ] : []),
    // Rechnungen - for office/admin
    ...(isOfficeOrAdmin ? [
      { text: 'Rechnungen', path: ROUTES.INVOICES, icon: <ReceiptIcon /> },
      ...(FEATURES.EMAIL ? [{ text: 'E-Mails', path: ROUTES.EMAILS, icon: <EmailIcon /> }] : [])
    ] : []),

    // SYSTEM section (admin only)
    ...(isAdmin ? [
      { type: 'divider' },
      { type: 'section', label: 'SYSTEM' },
      { text: 'Admin', path: ROUTES.ADMIN_PANEL, icon: <AdminPanelSettingsIcon /> }
    ] : []),
  ] : [
    { text: 'Home', path: ROUTES.HOME, icon: <HomeIcon /> },
  ];

  const handleLogout = () => {
    onClose();
    logout().then(() => navigate(ROUTES.LOGIN, { replace: true }));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '85%',
          maxWidth: 320,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          background: theme.palette.background.paper,
          backgroundImage: `linear-gradient(170deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
          boxShadow: `-5px 0 25px ${alpha(theme.palette.common.black, 0.2)}`,
        }
      }}
    >
      {/* Header with logo and close button */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <NavLogo />
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.secondary.main,
            bgcolor: alpha(theme.palette.secondary.main, 0.1),
            '&:hover': {
              bgcolor: alpha(theme.palette.secondary.main, 0.2),
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User info section (mobile) */}
      {currentUser && (
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            background: alpha(theme.palette.primary.main, 0.1),
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Avatar
            sx={{
              bgcolor: theme.palette.secondary.main,
              width: 48,
              height: 48,
              mr: 2,
              border: `2px solid ${alpha(theme.palette.common.white, 0.8)}`
            }}
          >
            {currentUser.username[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {currentUser.username}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {currentUser.role}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Navigation items */}
      <List sx={{ p: 2 }}>
        {mainNavItems.map((item, index) => {
          // Render section header
          if (item.type === 'section') {
            return (
              <ListSubheader
                key={`section-${index}`}
                sx={{
                  backgroundColor: 'transparent',
                  color: theme.palette.text.secondary,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: 0.5,
                  lineHeight: '32px',
                  px: 2,
                  py: 0.5,
                }}
              >
                {item.label}
              </ListSubheader>
            );
          }

          // Render divider
          if (item.type === 'divider') {
            return <Divider key={`divider-${index}`} sx={{ my: 2 }} />;
          }

          // Render navigation item
          return (
            <motion.div
              key={item.path}
              whileTap={{ scale: 0.95 }}
            >
              <ListItem
                component={Link}
                to={item.path}
                onClick={onClose}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  background: isActive(item.path)
                    ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`
                    : 'transparent',
                  color: isActive(item.path)
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  border: isActive(item.path)
                    ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    : '1px solid transparent',
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <ListItemIcon sx={{
                  color: isActive(item.path)
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  minWidth: 40
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 500
                  }}
                />
                {isActive(item.path) && (
                  <KeyboardArrowRightIcon
                    sx={{
                      color: theme.palette.primary.main,
                      opacity: 0.7
                    }}
                  />
                )}
              </ListItem>
            </motion.div>
          );
        })}

        <Divider sx={{ my: 2 }} />

        {/* Auth items for mobile */}
        {!currentUser ? (
          // Login option for non-authenticated users
          <ScaleAnimation>
            <ListItem
              component={Link}
              to={ROUTES.LOGIN}
              onClick={onClose}
              sx={{
                borderRadius: 2,
                mb: 1,
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.1),
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.main }}>
                <LoginIcon />
              </ListItemIcon>
              <ListItemText primary="Anmelden" />
            </ListItem>
          </ScaleAnimation>
        ) : (
          // Additional options for authenticated users (Profile is in bottom nav)
          <>
            <ScaleAnimation>
              <ThemeToggle variant="listitem" onClose={onClose} />
            </ScaleAnimation>

            <ScaleAnimation>
              <ListItem
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': {
                    background: alpha(theme.palette.secondary.main, 0.1),
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.secondary.main }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Abmelden" />
              </ListItem>
            </ScaleAnimation>
          </>
        )}
      </List>

      {/* Footer - Version number links to changelog */}
      <Box
        sx={{ mt: 'auto', p: 3, textAlign: 'center', cursor: 'pointer' }}
        onClick={() => { onClose(); navigate('/changelog'); }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
          v{APP_VERSION}
        </Typography>
      </Box>
    </Drawer>
  );
};

export default MobileDrawer;
